import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import type { Framing, NormalisedRect } from './faceFraming';
import { cropToPreFill } from './faceFraming';
import type { CameraErrorKind } from './useCameraStream';
import { useCameraStream } from './useCameraStream';
import { useFaceDetection } from './useFaceDetection';

export interface Capture {
  blob: Blob;
  crop: NormalisedRect | null;
}

interface WebcamCaptureProps {
  onCapture: (capture: Capture) => void;
  onError?: (kind: CameraErrorKind) => void;
  /** When set, the chosen camera is remembered under this localStorage key. */
  deviceStorageKey?: string;
  /** Locks the shutter (and its escape-hatch override) without touching the stream. */
  disabled?: boolean;
  /**
   * Leaving the camera, when the consumer offers that. Rendered as the last
   * cell of the control row rather than as a button of its own underneath:
   * three stacked centred controls, two of them identical blue links, is what
   * this replaces.
   */
  onCancel?: () => void;
}

/**
 * Grabs the current frame of `video` as a JPEG and hands it to `onBlob`.
 * Silent no-op when there is nothing to grab.
 */
function captureFrame(
  video: HTMLVideoElement | null,
  onBlob: (blob: Blob) => void,
): void {
  // Dimensions, not `readyState`: a zero-size canvas is the one thing that
  // genuinely cannot be captured (and in degraded mode `framing.ok` goes
  // true the instant `ready` flips, which can race a click ahead of the
  // video having decoded any). A camera that reports dimensions but never
  // advances past `readyState 1` — virtual cameras, OBS — must not be able
  // to silence the shutter for good; the operator gets a frame and can see
  // in the crop modal whether it is any good.
  if (!video || !video.videoWidth || !video.videoHeight) {
    return;
  }
  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return;
  }
  // Drawn unmirrored on purpose: the preview is mirrored for comfort, but an
  // identity photo must not be, or lanyard text reads backwards.
  ctx.drawImage(video, 0, 0);
  canvas.toBlob(
    (blob) => {
      // toBlob calls back null only on an encoding failure (e.g. a
      // zero-size or tainted canvas); the guard above rules out the
      // former. Deliberately silent rather than surfacing a confusing
      // error state for what should be an unreachable failure.
      if (blob) {
        onBlob(blob);
      }
    },
    'image/jpeg',
    0.95,
  );
}

function CameraErrorView({
  error,
  onRetry,
}: {
  error: CameraErrorKind;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex w-full flex-col items-center gap-3 border border-dashed border-rule bg-bg-soft p-6">
      <p className="text-sm text-ink">{t(`webcam.error.${error}`)}</p>
      {error !== 'insecure' && (
        <button type="button" className="btn-ghost" onClick={onRetry}>
          {t('webcam.retry')}
        </button>
      )}
    </div>
  );
}

function DevicePicker({
  devices,
  selectedDeviceId,
  onSelect,
}: {
  devices: MediaDeviceInfo[];
  selectedDeviceId: string;
  onSelect: (id: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <label className="label flex items-center gap-2">
      {t('webcam.device')}
      <select
        value={selectedDeviceId}
        onChange={(e) => onSelect(e.target.value)}
        className="bg-bg-soft text-ink"
      >
        {devices.map((device, index) => (
          <option key={device.deviceId} value={device.deviceId}>
            {device.label || `${t('webcam.device')} ${index + 1}`}
          </option>
        ))}
      </select>
    </label>
  );
}

function CameraPreview({
  videoRef,
  framing,
  detectionDown,
  ready,
}: {
  videoRef: RefObject<HTMLVideoElement | null>;
  framing: Framing;
  detectionDown: boolean;
  ready: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="relative w-full overflow-hidden border border-rule bg-ink">
      {/*
        Video and overlay are mirrored by ONE shared-parent transform, not
        two independent ones. `scaleX(-1)` reflects about an element's own
        centre: applied separately to the video and to the (differently
        positioned, differently sized) overlay box, each would reflect
        about its own centre and the overlay would render at its raw,
        unmirrored screen position while the video shows the mirrored feed
        — the box would not track the face at all. A single transform on
        their common ancestor reflects both about the same axis, so the
        face and the box move together. This also makes the wrapper the
        overlay's containing block (a transformed element always is one),
        so its left/top percentages resolve against the wrapper, matching
        the video's own size exactly.
      */}
      <div className="relative w-full -scale-x-100">
        <video ref={videoRef} muted playsInline className="block w-full" />

        {framing.crop && !detectionDown && (
          <div
            className="pointer-events-none absolute aspect-square [transition:left_90ms_linear,top_90ms_linear,width_90ms_linear]"
            style={{
              left: `${framing.crop.x * 100}%`,
              top: `${framing.crop.y * 100}%`,
              width: `${framing.crop.size * 100}%`,
              border: `2px solid ${framing.ok ? 'var(--green)' : 'var(--magenta)'}`,
            }}
          />
        )}
      </div>

      {!ready && (
        <p className="absolute inset-0 flex items-center justify-center text-sm text-inverse-fg">
          {t('webcam.starting')}
        </p>
      )}
    </div>
  );
}

function FramingHint({
  framing,
  detectionDown,
}: {
  framing: Framing;
  detectionDown: boolean;
}) {
  const { t } = useTranslation();
  const hintKey = framing.ok ? 'ready' : (framing.hint ?? 'noFace');
  const hint = detectionDown
    ? t('webcam.error.detection')
    : t(`webcam.hint.${hintKey}`);
  // aria-live: this line is the interaction — a screen-reader user has to
  // hear "step back" / "centre yourself" / "framing looks good", not
  // discover them by polling the DOM. Polite, because it changes as often
  // as the subject moves.
  return (
    <p
      aria-live="polite"
      className="label"
      style={{ color: framing.ok ? 'var(--green)' : 'var(--muted)' }}
    >
      {hint}
    </p>
  );
}

/**
 * One row of cells, hairlines between them, like every other action row
 * in this card. A grid is also what keeps the shutter still: it used to
 * share a centred flex row with the override, so every framing flip
 * re-centred the pair and slid the primary control 85px sideways under
 * the operator's cursor, driven by somebody else's movement. Cells do
 * not move.
 *
 * The override is live from the moment there is a frame to capture — no
 * arming delay, and not conditional on bad framing. Detection is an
 * assist and must never be the reason a photographer cannot take the
 * picture in front of them: the subject may be someone the detector
 * never resolves. Both cells call the same `shoot()`; the first states
 * the verdict, the second ignores it.
 */
function ShutterRow({
  shutterEnabled,
  overrideEnabled,
  onShoot,
  onCancel,
}: {
  shutterEnabled: boolean;
  overrideEnabled: boolean;
  onShoot: () => void;
  onCancel: (() => void) | undefined;
}) {
  const { t } = useTranslation();
  return (
    <div className="plate-strip three w-full">
      <button
        type="button"
        onClick={onShoot}
        disabled={!shutterEnabled}
        className="plate-act plate-act-go"
      >
        {t('webcam.shutter')}
      </button>
      <button
        type="button"
        onClick={onShoot}
        disabled={!overrideEnabled}
        className="plate-act"
      >
        {t('webcam.takeAnyway')}
      </button>
      {onCancel && (
        <button type="button" onClick={onCancel} className="plate-act">
          {t('common.cancel')}
        </button>
      )}
    </div>
  );
}

export function WebcamCapture({
  onCapture,
  onError,
  deviceStorageKey,
  disabled,
  onCancel,
}: WebcamCaptureProps) {
  const {
    videoRef,
    ready,
    error,
    framing,
    setFraming,
    devices,
    selectedDeviceId,
    selectDevice,
    retry,
  } = useCameraStream({ deviceStorageKey, onError });
  const detectionDown = useFaceDetection({
    videoRef,
    ready,
    framing,
    setFraming,
  });

  function shoot() {
    // The rule and its reasoning live with the rest of the framing maths,
    // where they can be tested; see `cropToPreFill`.
    captureFrame(videoRef.current, (blob) => {
      onCapture({ blob, crop: cropToPreFill(framing) });
    });
  }

  if (error) {
    return <CameraErrorView error={error} onRetry={retry} />;
  }

  // The gate stays honest: it reflects live framing only. The escape link
  // (below) is the sole override once armed. `disabled` is ANDed in on top
  // of both — a consumer that keeps this component mounted with no target
  // selected (the kiosk) must not let either path fire `shoot()`.
  const shutterEnabled = ready && framing.ok && !disabled;
  const overrideEnabled = ready && !disabled;

  return (
    <div className="@container flex w-full flex-col items-center gap-3">
      {/*
        Choosing a camera is session setup, not part of the shoot loop, so it
        belongs before the frame rather than between the framing hint and the
        shutter that hint gates. Sitting there, a control touched once per
        session pushed the shutter down the card and broke the one line an
        operator actually reads top to bottom: frame, verdict, press.
      */}
      {devices.length > 1 && selectedDeviceId && (
        <DevicePicker
          devices={devices}
          selectedDeviceId={selectedDeviceId}
          onSelect={selectDevice}
        />
      )}

      <CameraPreview
        videoRef={videoRef}
        framing={framing}
        detectionDown={detectionDown}
        ready={ready}
      />

      <FramingHint framing={framing} detectionDown={detectionDown} />

      <ShutterRow
        shutterEnabled={shutterEnabled}
        overrideEnabled={overrideEnabled}
        onShoot={shoot}
        onCancel={onCancel}
      />
    </div>
  );
}
